'use client';

import { accountsService } from '@/@account/services';
import { useAccountViewAsTarget } from '@/@subsidiary/hooks/useAccountViewAsTarget';
import { useSession } from '@/providers/enhanced-session';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Effective org fields for CMS tile filtering / route disable while View-as
 * (or the signed-in home org when not masquerading).
 */
export type EffectiveOrgUiContext = {
    isViewAsActive: boolean;
    orgName: string;
    isLicenseValid: boolean;
    /**
     * A paid credits plan (M4S-5669): the View-as client's when viewing as one, else the session
     * org's. Missing until the API sends it. Read only through `hasStudioEntitlement`.
     */
    hasPaidCreditsPlan: boolean;
    /** Client-wide Studio AI trial; home session value when not View-as. */
    hasClientStudioAIAccess: boolean;
    /**
     * For CMS tile / sidebar filtering: false while Viewing-as so internal-only
     * tiles (e.g. Feasibility) hide and client-scoped cards match the target org.
     */
    isInternalForTiles: boolean;
};

export const VIEW_AS_ORG_QUERY_KEY = (sfAccountId: string) =>
    ['viewAs', 'organization', sfAccountId] as const;

/**
 * Resolves org name + license for home/sidebar tiles under View-as.
 * Uses seed orgName immediately (no blocking spinner). License fetch is
 * background-only and shared across callers via React Query.
 */
export function useEffectiveOrgUiContext(): EffectiveOrgUiContext {
    const { data: session } = useSession();
    const viewAsTarget = useAccountViewAsTarget();
    const isViewAsActive = viewAsTarget != null;

    const viewingOrgQuery = useQuery({
        queryKey: VIEW_AS_ORG_QUERY_KEY(viewAsTarget?.sfAccountId ?? 'home'),
        queryFn: () => accountsService.getOrganization(viewAsTarget!.sfAccountId),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        enabled: isViewAsActive && !!viewAsTarget?.sfAccountId,
        // Don't refetch on every mount/focus — tiles only need name + license
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    const sessionIsInternal = session?.user?.isInternal === true;
    const orgName = isViewAsActive
        ? (viewingOrgQuery.data?.orgName ?? viewAsTarget?.orgName ?? '')
        : (session?.org?.orgName ?? '');
    const isLicenseValid = isViewAsActive
        ? viewingOrgQuery.data?.isLicenseValid === true
        : (session?.org?.isLicenseValid ?? false);
    const hasPaidCreditsPlan = isViewAsActive
        ? viewingOrgQuery.data?.hasPaidCreditsPlan === true
        : session?.org?.hasPaidCreditsPlan === true;
    const hasClientStudioAIAccess = isViewAsActive
        ? false
        : (session?.org?.hasClientStudioAIAccess ?? false);
    const isInternalForTiles = isViewAsActive ? false : sessionIsInternal;

    return useMemo(
        () => ({
            isViewAsActive,
            orgName,
            isLicenseValid,
            hasPaidCreditsPlan,
            hasClientStudioAIAccess,
            isInternalForTiles,
        }),
        [
            isViewAsActive,
            orgName,
            isLicenseValid,
            hasPaidCreditsPlan,
            hasClientStudioAIAccess,
            isInternalForTiles,
        ],
    );
}
